import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft, Users, Clock, MapPin, Megaphone, Copy, CheckCircle2, Loader,
  UserCheck, ListChecks, Send, Calendar,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SkillConfidenceBadge from "@/components/SkillConfidenceBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMAT_LABELS = {
  open_play: "Open Play", doubles: "Doubles Match", practice: "Practice / Drill",
  mixer: "Skill-Matched Mixer", ladder_lite: "Ladder-Lite",
};

const announceSchema = z.object({
  message: z.string().trim().min(5, "Message must be at least 5 characters").max(500, "Keep under 500 characters"),
});

export default function OrganizerSessionDetailPage() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attendance, setAttendance] = useState(new Set());
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [attendanceSaved, setAttendanceSaved] = useState(false);
  const [posting, setPosting] = useState(false);
  const [dupDate, setDupDate] = useState("");
  const [dupOk, setDupOk] = useState("");

  const { register, handleSubmit, reset, formState: { errors } } = useForm({ resolver: zodResolver(announceSchema) });

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/organizer/sessions/${sessionId}`, { credentials: "include" });
      if (!r.ok) { setError(r.status === 403 ? "This isn't your session." : "Session not found."); setLoading(false); return; }
      const d = await r.json();
      setSession(d);
      setAttendance(new Set(d.attendees || []));
    } catch { setError("Failed to load session."); }
    finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  const toggleAttendance = (id) => {
    setAttendance((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const saveAttendance = async () => {
    setSavingAttendance(true); setAttendanceSaved(false);
    try {
      const r = await fetch(`${API}/organizer/sessions/${sessionId}/attendance`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify([...attendance]),
      });
      if (r.ok) { setAttendanceSaved(true); await load(); setTimeout(() => setAttendanceSaved(false), 2500); }
    } finally { setSavingAttendance(false); }
  };

  const postAnnouncement = async ({ message }) => {
    setPosting(true);
    try {
      const r = await fetch(`${API}/organizer/sessions/${sessionId}/announce`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ message }),
      });
      if (r.ok) { reset({ message: "" }); await load(); }
    } finally { setPosting(false); }
  };

  const duplicate = async () => {
    if (!dupDate.trim()) return;
    const r = await fetch(`${API}/organizer/sessions/${sessionId}/duplicate`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify({ new_date_time: dupDate }),
    });
    if (r.ok) { setDupOk("Session duplicated to your dashboard."); setDupDate(""); setTimeout(() => setDupOk(""), 3000); }
  };

  if (!user) return <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center px-4 dark-section"><p className="text-gray-400">Please <Link to="/login" className="text-brand-green hover:underline">log in</Link>.</p></div>;
  if (loading) return <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center dark-section"><Loader size={28} className="text-brand-green animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#0F1629] pt-24 flex items-center justify-center px-4 dark-section">
      <div className="text-center">
        <p className="text-gray-300 mb-4">{error}</p>
        <Link to="/organizer" className="text-brand-green hover:underline text-sm">Back to dashboard</Link>
      </div>
    </div>
  );

  const participants = session.participant_details || [];
  const waitlist = session.waitlist_details || [];
  const announcements = [...(session.announcements || [])].reverse();
  const cap = session.capacity || 0;
  const filled = session.participants_count || 0;
  const pct = cap ? Math.min(100, Math.round((filled / cap) * 100)) : 0;

  return (
    <div className="min-h-screen bg-[#0F1629] pt-20 pb-16 dark-section" data-testid="organizer-session-detail">
      <div className="max-w-4xl mx-auto px-4">
        <Link to="/organizer" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-4" data-testid="back-to-organizer-btn">
          <ArrowLeft size={15} /> Back to dashboard
        </Link>

        {/* Header */}
        <div className="bg-[#161B28] border border-white/8 rounded-2xl p-6 mb-6 dark-section">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-brand-green/10 text-brand-green border border-brand-green/30 capitalize">{session.sport}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-gray-300 border border-white/10">{FORMAT_LABELS[session.session_type] || session.session_type || session.format}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${session.status === "open" ? "bg-brand-green/10 text-brand-green border-brand-green/30" : session.status === "completed" ? "bg-white/5 text-gray-400 border-white/10" : "bg-yellow-400/10 text-yellow-400 border-yellow-400/30"}`}>{session.status}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-black text-white mb-3">{session.title}</h1>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-gray-400">
            <span className="flex items-center gap-1.5"><Clock size={14} /> {session.date_time}</span>
            <span className="flex items-center gap-1.5"><MapPin size={14} /> {session.location}</span>
            <span className="flex items-center gap-1.5"><Users size={14} /> {filled}/{cap} joined · {session.waitlist_count || 0} waitlisted</span>
          </div>
          {/* Capacity bar */}
          <div className="mt-4">
            <div className="h-2 rounded-full bg-white/8 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Capacity filled">
              <div className="h-full bg-brand-green rounded-full transition-all" style={{ width: `${pct}%` }} data-testid="capacity-bar" />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Attendees + attendance */}
          <div className="bg-[#161B28] border border-white/8 rounded-2xl p-5 dark-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-white flex items-center gap-2"><ListChecks size={16} className="text-brand-green" /> Attendees ({participants.length})</h2>
            </div>
            {participants.length === 0 ? (
              <p className="text-gray-500 text-sm py-6 text-center">No one has joined yet.</p>
            ) : (
              <div className="space-y-2" data-testid="attendee-list">
                {participants.map((p) => (
                  <label key={p.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#1A2035] cursor-pointer hover:bg-[#1f2740] transition-colors dark-section" data-testid="attendee-item">
                    <input type="checkbox" checked={attendance.has(p.id)} onChange={() => toggleAttendance(p.id)}
                      className="w-4 h-4 accent-[#2ED47A]" data-testid={`attendance-checkbox-${p.id}`} aria-label={`Mark ${p.name} present`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{p.name}</p>
                      <p className="text-xs text-gray-500 truncate">{p.city}</p>
                    </div>
                    {p.skill_confidence && <SkillConfidenceBadge level={p.skill_confidence.level} label={p.skill_confidence.label} size="xs" variant="dark" />}
                    {attendance.has(p.id) && <UserCheck size={15} className="text-brand-green flex-shrink-0" />}
                  </label>
                ))}
              </div>
            )}
            {participants.length > 0 && (
              <div className="flex items-center gap-3 mt-4">
                <button onClick={saveAttendance} disabled={savingAttendance}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green text-brand-navy text-sm font-bold rounded-xl hover:bg-brand-green/90 transition-colors disabled:opacity-50"
                  data-testid="save-attendance-btn">
                  {savingAttendance ? <Loader size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Mark Attendance
                </button>
                {attendanceSaved && <span className="text-sm text-brand-green flex items-center gap-1" data-testid="attendance-saved-msg"><CheckCircle2 size={14} /> Saved</span>}
              </div>
            )}

            {/* Waitlist */}
            {waitlist.length > 0 && (
              <div className="mt-6 pt-5 border-t border-white/8">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Waitlist ({waitlist.length})</h3>
                <div className="space-y-2" data-testid="waitlist-list">
                  {waitlist.map((w) => (
                    <div key={w.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#1A2035] dark-section" data-testid="waitlist-item">
                      <span className="text-sm text-gray-300 truncate">{w.name}</span>
                      <span className="text-xs text-yellow-400">waitlisted</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Announcements + actions */}
          <div className="space-y-6">
            <div className="bg-[#161B28] border border-white/8 rounded-2xl p-5 dark-section">
              <h2 className="text-sm font-bold text-white flex items-center gap-2 mb-4"><Megaphone size={16} className="text-brand-orange" /> Announcements</h2>
              <form onSubmit={handleSubmit(postAnnouncement)} className="mb-4" noValidate>
                <textarea rows={3} {...register("message")} placeholder="Post an update to everyone in this session…"
                  className="w-full bg-[#1A2035] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green/40 resize-none dark-section"
                  data-testid="announcement-input" aria-invalid={!!errors.message} />
                {errors.message && <p className="mt-1 text-xs text-red-400" role="alert">{errors.message.message}</p>}
                <button type="submit" disabled={posting}
                  className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-brand-orange text-white text-sm font-bold rounded-xl hover:bg-brand-orange/90 transition-colors disabled:opacity-50"
                  data-testid="post-announcement-btn">
                  {posting ? <Loader size={14} className="animate-spin" /> : <Send size={14} />} Post to all
                </button>
              </form>
              {announcements.length === 0 ? (
                <p className="text-gray-500 text-sm">No announcements yet.</p>
              ) : (
                <div className="space-y-3" data-testid="announcements-list">
                  {announcements.map((a, i) => (
                    <div key={i} className="px-3 py-2.5 rounded-xl bg-[#1A2035] border border-white/5 dark-section" data-testid="announcement-item">
                      <p className="text-sm text-gray-200">{a.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{a.sent_by} · {a.sent_at ? new Date(a.sent_at).toLocaleString() : ""}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Duplicate */}
            <div className="bg-[#161B28] border border-white/8 rounded-2xl p-5 dark-section">
              <h2 className="text-sm font-bold text-white flex items-center gap-2 mb-3"><Copy size={16} className="text-brand-green" /> Duplicate Session</h2>
              <p className="text-xs text-gray-500 mb-3">Create a fresh copy with a new date. Attendees reset.</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input value={dupDate} onChange={(e) => setDupDate(e.target.value)} placeholder="Saturday Feb 1 @ 10am"
                    className="w-full bg-[#1A2035] border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green/40 dark-section"
                    data-testid="duplicate-date-input" />
                </div>
                <button onClick={duplicate} disabled={!dupDate.trim()}
                  className="px-4 py-2.5 border border-white/15 text-white text-sm font-medium rounded-xl hover:border-white/25 transition-colors disabled:opacity-40"
                  data-testid="duplicate-session-btn">Duplicate</button>
              </div>
              {dupOk && <p className="text-sm text-brand-green mt-2" data-testid="duplicate-ok-msg">{dupOk}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
